import {
  AssetRoutine,
  AssetRoutineState,
  FIND_ROUTINE_DRAFTS_BY_ASSET,
  Issue,
  RoutineConfig,
  RoutineWorkflow,
  Severity,
  useCurrentAccount,
  useCurrentWorkspace,
  useFindByQuery,
  WorkspaceAsset,
  WorkspaceRoutine,
  WorkspaceType,
} from '@gripp/shared-logic';
import { router } from 'expo-router';
import { extractAndDeduplicateRoutines } from 'libs/shared-ui/src/utils/assetHelpers';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleProp,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { Button } from '../../crop-circle/Button';
import { AddIcon, Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import LoadingIndicator from '../../loadingIndicator';
import { RoutineModule } from '../types';
import { RoutineModuleButton } from './routineModuleButton';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type RoutineButtonsProps = {
  workspaceAsset: WorkspaceAsset;
  showAll: boolean;
  initialRoutinesToShow: number;
  hideAssignRoutinesButton?: boolean;
  onRoutineSelected: (props: RoutineExecutionProps) => void;
  onNavigateToRoutineEdit?: () => void;
  buttonStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  buttonIcon?: Severity;
  buttonIconStyle?: StyleProp<ViewStyle>;
  buttonsContainerStyle?: StyleProp<ViewStyle>;
  selectedRoutineId?: string;
  onItemsCountChange?: (count: number) => void;
  searchTerm?: string;
};

export type RoutineExecutionProps = {
  workspaceAsset: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  sourceIssue?: Issue;
  workflows?: RoutineWorkflow[];
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onCanceled?: () => void;
  onCompleted?: () => void;
  autoPromptQueue?: RoutineExecutionProps[];
};

export const RoutineButtons = (props: RoutineButtonsProps) => {
  const { workspaceAsset, showAll, initialRoutinesToShow } = props;
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
  const { t } = useTranslation();

  const userRoleIds = useMemo(
    () => account?.roles?.map((r) => r.id) || [],
    [account?.roles]
  );

  const isRoutinePermitted = useCallback(
    (routine: WorkspaceRoutine) => {
      const isGlobalAdmin = account?.isGlobalAdmin();
      const permittedRoles = (routine.config as any)?.permittedRoles;

      if (isGlobalAdmin) return true;
      if (!permittedRoles || permittedRoles.length === 0) return true;
      return permittedRoles.some((roleId: string) =>
        userRoleIds.includes(roleId)
      );
    },
    [account, userRoleIds]
  );

  const orFilters: any[] = [
    { workspaceAsset: { eq: workspaceAsset.id } },
    { group: { in: workspaceAsset.groups?.map((g) => g.id) || [] } },
  ];

  if (isOEM && workspaceAsset.asset?.product?.id) {
    orFilters.push({ product: { eq: workspaceAsset.asset.product.id } });
  }

  const { data: workflowData, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        or: orFilters,
        and: {
          disabled: {
            ne: true,
          },
        },
      },
    },
  });

  const { data: draftsData } = useFindByQuery({
    modelName: 'assetRoutine',
    query: FIND_ROUTINE_DRAFTS_BY_ASSET,
    variables: {
      filter: {
        workspaceAsset: { eq: workspaceAsset?.id },
        isDraft: { eq: true },
      },
    },
    options: {
      enabled: !!workspaceAsset?.id,
    },
  });

  const draftRoutineIds = useMemo(() => {
    const ids = new Set<string>();
    draftsData?.items?.forEach((item: any) => {
      if (item.routine?.id) ids.add(item.routine.id);
    });
    return ids;
  }, [draftsData]);

  const isOneTime = (routine: WorkspaceRoutine) =>
    !!(routine.config as any)?.oneTimeOnly;

  const hasState = useCallback(
    (routine: WorkspaceRoutine, asset: WorkspaceAsset) =>
      workspaceAsset.routineState?.some(
        (s) => s.workspaceRoutineId === routine.id
      ),
    [workspaceAsset.routineState]
  );

  const buttonData = useMemo(() => {
    const allExternalRoutines: WorkspaceRoutine[] =
      extractAndDeduplicateRoutines(workspaceAsset);
    const workspaceRoutines = workspaceAsset.routines || [];

    if (!workspaceRoutines.length && !allExternalRoutines.length) {
      return [];
    }

    // Filter routines based on user permissions
    const filterByPermissions = (routines: WorkspaceRoutine[]) =>
      routines.filter(isRoutinePermitted);

    // Filter routines for one-time behavior: show pending one-time routines, hide completed ones
    const filterOneTimeRoutines = (routines: WorkspaceRoutine[]) => {
      const active = routines.filter((r) => !r.deactivated);
      const pendingOneTime = active.filter(
        (r) => isOneTime(r) && !hasState(r, workspaceAsset)
      );
      // If there are pending one-time routines, show only those. Otherwise, hide completed one-time routines
      return pendingOneTime.length > 0
        ? pendingOneTime
        : active.filter((r) => !isOneTime(r) || !hasState(r, workspaceAsset));
    };

    // Get order for a routine, checking asset-level routineOrder first, then product's routineOrder, then falling back to inherent order
    const getRoutineOrder = (routine: WorkspaceRoutine) => {
      const assetOrder = workspaceAsset.routineOrder?.find(
        (x) => x.workspaceRoutineId === routine.id
      );
      if (assetOrder) return assetOrder.order;

      const productOrder = (
        workspaceAsset.asset?.product as any
      )?.routineOrder?.find((x: any) => x.workspaceRoutineId === routine.id);
      if (productOrder) return productOrder.order;

      return routine.order;
    };

    // Sort routines and map to button data format
    const toButtonData = (routines: WorkspaceRoutine[]) =>
      routines
        .sort((a, b) => getRoutineOrder(a) - getRoutineOrder(b))
        .map((routine) => ({
          routine,
          state: workspaceAsset.routineState?.find(
            (s) => s.workspaceRoutineId === routine.id
          ),
        }));

    const permittedExternal = filterByPermissions(allExternalRoutines);
    const permittedWorkspace = filterByPermissions(workspaceRoutines);

    // Combine all permitted routines, deduplicate, then apply one-time logic to the full pool
    const allPermitted = uniqBy(
      [...permittedExternal, ...permittedWorkspace],
      'id'
    );
    const visibleRoutines = filterOneTimeRoutines(allPermitted);

    const combineRoutines = uniqBy(toButtonData(visibleRoutines), 'routine.id');

    if (combineRoutines.length > initialRoutinesToShow && !showAll) {
      return combineRoutines.slice(0, initialRoutinesToShow);
    }

    return combineRoutines;
  }, [
    workspaceAsset,
    showAll,
    initialRoutinesToShow,
    hasState,
    isRoutinePermitted,
  ]);

  const filteredButtonData = useMemo(() => {
    if (!props.searchTerm) return buttonData;
    const term = props.searchTerm.toLowerCase();
    return buttonData.filter((data: any) => {
      const config = data.routine?.config;
      const label = config?.routineButtonText || config?.title;
      if (label && label.toLowerCase().includes(term)) return true;
      return data.routine?.name?.toLowerCase().includes(term) ?? false;
    });
  }, [buttonData, props.searchTerm]);

  useEffect(() => {
    props.onItemsCountChange?.(buttonData.length);
  }, [buttonData.length, props.onItemsCountChange]);

  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig, state: any) => {
      if (!id || !workflowData?.items?.length) return [];

      return workflowData.items
        .filter(
          (item: RoutineWorkflow) =>
            item.trigger?.config?.workspaceRoutineId === id
        )
        .map((workflow: RoutineWorkflow) => {
          return { ...workflow, uom: config?.uom, count: state?.count };
        });
    },
    [workflowData]
  );

  const onClick = (
    routine: WorkspaceRoutine,
    module: RoutineModule<any, any>,
    state?: AssetRoutineState<any>,
    additionalState?: any,
    workflows?: RoutineWorkflow[]
  ) => {
    props.onRoutineSelected({
      workspaceAsset,
      routine,
      module,
      state,
      additionalState,
      workflows,
    });
  };

  const Buttons = () => {
    if (filteredButtonData?.length === 0) {
      // Hide the empty-state "Add Routines" button for viewers without
      // permission to manage routines (e.g. the anonymous asset page).
      if (props.hideAssignRoutinesButton) {
        return null;
      }
      return (
        <>
          <Button
            style={styles.addFileEmpty}
            leftIcon={<AddIcon />}
            variant={'rectangular'}
            onPress={() => {
              if (Platform.OS === 'web') {
                props.onNavigateToRoutineEdit?.();
              } else {
                router.push(`/assets/${workspaceAsset.asset.id}/routines-edit`);
              }
            }}
          >
            {t('asset.add')} {t('asset.header.buttons.routines')}
          </Button>
        </>
      );
    }

    return filteredButtonData.map((data: any, index: number) => {
      const workspaceRoutineId = data.routine?.id;

      const config = data.routine?.config;
      const state = data.state?.state;

      const workflows = getRoutineWorkflows(
        workspaceRoutineId ?? '',
        config,
        state
      );

      const hasWorkflows = workflows.length > 0;

      const hasDraft = draftRoutineIds.has(workspaceRoutineId || '');

      const HasWorkflows = hasWorkflows && (
        <Icon name="Alert" color={Colors.role.textStrong} size={22} />
      );

      const secondaryButtonIcon = (
        <>
          {hasDraft && (
            <Icon
              name="Edit"
              color={Colors.role.textStrong}
              size={20}
              style={{ marginRight: 4 }}
            />
          )}
          {HasWorkflows}
        </>
      );

      return (
        <RoutineModuleButton
          key={`routine-button-${index}`}
          routine={data.routine}
          state={data.state as AssetRoutineState<any>}
          buttonIndex={index}
          buttonCount={filteredButtonData.length}
          buttonStyle={props.buttonStyle}
          labelStyle={[props.labelStyle, { flexShrink: 1 }]}
          onClick={onClick}
          secondaryButtonIcon={secondaryButtonIcon}
          workflows={workflows}
          isSelected={
            !!(
              props.selectedRoutineId &&
              props.selectedRoutineId === data.routine.id
            )
          }
        />
      );
    });
  };

  return (
    <ScrollView
      scrollEnabled={showAll}
      showsVerticalScrollIndicator={false}
      style={
        props.buttonsContainerStyle
          ? props.buttonsContainerStyle
          : styles.scrollView
      }
    >
      {isLoading ? <LoadingIndicator flexContainer /> : <Buttons />}
    </ScrollView>
  );
};

const styles = themedStyleSheet((role) => ({
  scrollView: {
    flexGrow: 0,
  },
  addFileEmpty: {
    height: 56,
    minHeight: 56,
    backgroundColor: role.surfaceInverseSoft,
  },
}));

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
  }
`;
